import { type KLCollectibleId } from '../../modules/koh-lanta/types/collectible';
import { type StateType } from '../../states';
import {
  type BasketState,
  type BasketItems,
  type BasketFreebetsState,
  type BasketBonusesState,
  type BasketCachedMMAPIResponsesInfosState,
  type BasketMonopolyCardState,
  type BasketSystems,
  type BasketEligibleBonusState,
  type BasketErrorState,
  type BasketBoomState,
  type BasketItemState,
  type BasketItemErrorState,
  type BasketItemEligibleFreebetsState,
} from '../../states/basket';
import { type BasketEligibleBoosterState } from '../../states/basket';
import { type BasketBoostersState } from '../../states/basket';
import { type BetBuilderItem } from '../../states/betbuilder';
import { type BasketItemStatus } from '../../types/basket';
import { type BasketMode, type BasketStatus } from '../../types/basket';
import { type Selector } from '../../types/selectors';
import { getItemIdOrOddIdFromProps } from '../_propsGetters_';

const emptyObject = {};
const emptyArray: [] = [];

export const selectBasketState: Selector<BasketState> = (state: StateType) => state.basket ?? emptyObject;

//#region state.basket
export const selectBasketItems: Selector<BasketItems> = (state: StateType) =>
  selectBasketState(state).items ?? emptyObject;

export const selectBasketMode: Selector<BasketMode> = (state: StateType) => selectBasketState(state).mode;

export const selectBasketStatus: Selector<BasketStatus> = (state: StateType) => selectBasketState(state).status;

export const selectBasketDefaultStake: Selector<string | number> = (state: StateType) =>
  selectBasketState(state).defaultStake;

export const selectBasketStake: Selector<string | number> = (state: StateType) => selectBasketState(state).stake;

export const selectBasketFreebets: Selector<BasketFreebetsState> = (state: StateType) =>
  selectBasketState(state).freebets;

export const selectBasketSelectedBonusId: Selector<number | undefined> = (state: StateType) =>
  selectBasketState(state).selectedBonusId ?? false;

export const selectBasketBonuses: Selector<BasketBonusesState> = (state: StateType) => selectBasketState(state).bonuses;

export const selectBasketEligibleBonuses: Selector<Array<BasketEligibleBonusState>> = (state: StateType) =>
  selectBasketState(state).eligibleBonuses;

export const selectBasketBoostersState: Selector<BasketBoostersState> = (state: StateType) =>
  selectBasketState(state).boosters;

export const selectBasketItemsMemoizedForSelection: Selector<BasketItems> = (state: StateType) =>
  selectBasketState(state).itemsMemoizedForSelection;

export const selectBasketLoading: Selector<boolean> = (state: StateType) => selectBasketState(state).loading ?? false;

export const selectBasketIsCashout: Selector<boolean> = (state: StateType) =>
  selectBasketState(state).isCashout ?? false;

export const selectBasketIsKeyboardVisible: Selector<boolean> = (state: StateType) =>
  selectBasketState(state).isKeyboardVisible ?? false;

export const selectBasketEligibleKLCollectibleId: Selector<KLCollectibleId | null> = (state: StateType) =>
  selectBasketState(state).eligibleKLCollectibleId ?? null;

export const selectBasketIsGRActive: Selector<boolean> = (state: StateType) =>
  selectBasketState(state).isGRActive ?? false;

export const selectBasketIsMonopolyActive: Selector<boolean> = (state: StateType) =>
  selectBasketState(state).isMonopolyActive ?? false;

export const selectBasketIsBoomActive: Selector<boolean> = (state: StateType) =>
  selectBasketState(state).isBoomActive ?? false;

export const selectBasketCachedMMAPIResponsesInfos: Selector<BasketCachedMMAPIResponsesInfosState> = (
  state: StateType,
) => selectBasketState(state).cachedMMAPIResponsesInfos ?? emptyObject;

export const selectBasketIsFromFreebets: Selector<boolean> = (state: StateType) =>
  selectBasketState(state).isFromFreebets ?? false;

export const selectBasketMonopolyCard: Selector<BasketMonopolyCardState | null> = (state: StateType) =>
  selectBasketState(state).monopolyCard ?? null;

export const selectBasketIsFromLineUp: Selector<boolean> = (state: StateType) =>
  selectBasketState(state).isFromLineUp ?? false;

export const selectBasketBoom: Selector<BasketBoomState | null> = (state: StateType) =>
  selectBasketState(state).boom ?? null;

export const selectBasketSelectedFreebetId: Selector<number | boolean | undefined> = (state: StateType) =>
  selectBasketState(state).selectedFreebetId;

export const selectBasketEligibleBooster: Selector<BasketEligibleBoosterState> = (state: StateType) =>
  selectBasketState(state).eligibleBooster ?? null;

export const selectBasketSystems: Selector<BasketSystems> = (state: StateType) =>
  selectBasketState(state).systems ?? null;

export const selectBasketBetslipId: Selector<string | null | undefined> = (state: StateType) =>
  selectBasketState(state).betslipId;

export const selectBasketError: Selector<BasketErrorState> = (state: StateType) =>
  selectBasketState(state).error ?? null;

export const selectBasketAdditionalMessage: Selector<string | undefined> = (state: StateType) =>
  selectBasketState(state).additionalMessage;

export const selectBasketIsLoading: Selector<boolean> = (state: StateType) => selectBasketState(state).loading ?? false;
//#endregion

//#region state.basket.items
type BasketItemsSelectorProps = {
  itemId?: string | number;
  oddId?: string | number;
  oddsId?: string | number;
};

export const selectBasketItemsEntity: Selector<BasketItemState | undefined> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => {
  const basketItems = selectBasketItems(state);
  const id = getItemIdOrOddIdFromProps(state, props);

  if (id) {
    // $FlowFixMe
    return basketItems[id];
  }
};
//#endregion

//#region state.basket.items[itemId]
export const selectBasketItemsEntityStatus: Selector<BasketItemStatus | undefined> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.status;

export const selectBasketItemsEntityOddId: Selector<number | undefined> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.oddId;

export const selectBasketItemsEntityMatchId: Selector<number | undefined> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.matchId;

export const selectBasketItemsEntityBetId: Selector<number | undefined> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.betId;

export const selectBasketItemsEntityValue: Selector<number | undefined> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.value;

export const selectBasketItemsEntitySportId: Selector<number | undefined> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.sportId;

export const selectBasketItemsEntityBanker: Selector<boolean> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.banker ?? false;

export const selectBasketItemsEntityStake: Selector<string | number | undefined> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.stake;

export const selectBasketItemsEntitySortBy: Selector<number | undefined> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.sortBy;

export const selectBasketItemsEntityIsGRActive: Selector<boolean> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.isGRActive ?? false;

export const selectBasketItemsEntityIsFromLineUp: Selector<boolean> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.isFromLineUp ?? false;

export const selectBasketItemsEntityIsFromMultichoice: Selector<boolean> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.isFromMultichoice ?? false;

export const selectBasketItemsEntityIsBoomActive: Selector<boolean> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.isBoomActive ?? false;

export const selectBasketItemsEntityBoomState: Selector<BasketBoomState | null> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.boom ?? null;

export const selectBasketItemsEntityChanged: Selector<boolean> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.changed ?? false;

export const selectBasketItemsEntityError: Selector<BasketItemErrorState | null> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.error ?? null;

export const selectBasketItemsEntityBetslipId: Selector<string | null> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.betslipId ?? null;

export const selectBasketItemsEntityAdditionalMessage: Selector<string | null> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.additionalMessage ?? null;

export const selectBasketItemsEntitySelectedFreebetId: Selector<number | boolean | undefined> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.selectedFreebetId;

export const selectBasketItemsEntityOutcomeId: Selector<number | undefined> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.outcomeId;

export const selectBasketItemsEntityEligibleBonuses: Selector<Array<BasketEligibleBonusState>> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.eligibleBonuses ?? emptyArray;

export const selectBasketItemsEntityEligibleFreebets: Selector<BasketItemEligibleFreebetsState | null> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.eligibleFreebets ?? null;

export const selectBasketItemsEntityEligibleKLCollectibleId: Selector<KLCollectibleId | undefined> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.eligibleKLCollectibleId;

export const selectBasketItemsEntitySelectedBonusId: Selector<number | undefined> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.selectedBonusId;

export const selectBasketItemsEntityBetbuilder: Selector<boolean> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.betbuilder ?? false;

export const selectBasketItemsEntityCashout: Selector<boolean> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.cashout ?? false;

export const selectBasketItemsEntityValidatedValue: Selector<number | undefined> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.validatedValue;

export const selectBasketItemsEntityIsMonopolyActive: Selector<boolean> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.isMonopolyActive ?? false;

export const selectBasketItemsEntitySelectionItems: Selector<Array<BetBuilderItem> | undefined> = (
  state: StateType,
  props: BasketItemsSelectorProps = {},
) => selectBasketItemsEntity(state, props)?.selectionItems;
//#endregion
